/// <amd-module name="Build/Admin/Common/Services/PoolAdminLinkingService" />

import { TaskAgentReference, TaskAgentQueue, TaskAgentJobRequest, TaskAgentPool, ElasticPool } from "DistributedTask/Client/Contracts/TaskAgent";

import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";

import { Routes, RouteValues, QueryParams, PlanTypes, PoolHubViewKeys } from "Build/Admin/Common/Constants";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";

export interface IPoolAdminLinkingService extends IVssService {
    getAccountPoolsUrl(): string;
    getProjectPoolsUrl(): string;
    getAgentQueueUrl(agentQueue: TaskAgentQueue): string;
    getAgentPoolUrl(pool: TaskAgentPool): string;
    getAgentUrl(queue: TaskAgentQueue | undefined, pool: TaskAgentPool | undefined, agentRef: TaskAgentReference): string;
    getJobResultsUrl(jobRequest: TaskAgentJobRequest): string;
    getQueueChecksUrl(agentQueue: TaskAgentQueue): string;
    getServiceConnectionUrl(elasticPool: ElasticPool): string;
}

class PoolAdminLinkingService extends VssService implements IPoolAdminLinkingService {
    public getAccountPoolsUrl(): string {
        const routeValues: { [key: string]: string } = this._getDefaultRouteValues();
        return routeUrl(this.pageContext, Routes.AccountSettingsRouteId, routeValues);
    }

    public getProjectPoolsUrl(): string {
        const routeValues: { [key: string]: string } = this._getDefaultRouteValues();
        return routeUrl(this.pageContext, Routes.ProjectSettingsRouteId, routeValues);
    }

    public getAgentQueueUrl(queue: TaskAgentQueue): string {
        const routeValues: { [key: string]: string } = this._getDefaultRouteValues();

        routeValues[QueryParams.QueueId] = queue.id.toString();

        return routeUrl(this.pageContext, Routes.ProjectSettingsRouteId, routeValues);
    }

    public getAgentPoolUrl(pool: TaskAgentPool): string {
        const routeValues: { [key: string]: string } = this._getDefaultRouteValues();

        routeValues[QueryParams.PoolId] = pool.id.toString();

        return routeUrl(this.pageContext, Routes.AccountSettingsRouteId, routeValues);
    }

    public getAgentUrl(queue: TaskAgentQueue | undefined, pool: TaskAgentPool | undefined, agentRef: TaskAgentReference): string {
        const routeValues: { [key: string]: string } = this._getDefaultRouteValues();
        routeValues[QueryParams.AgentId] = agentRef.id.toString();

        if (queue) {
            routeValues[QueryParams.QueueId] = queue.id.toString();
            return routeUrl(this.pageContext, Routes.ProjectSettingsRouteId, routeValues);
        }
        else if (pool) {
            routeValues[QueryParams.PoolId] = pool.id.toString();
            return routeUrl(this.pageContext, Routes.AccountSettingsRouteId, routeValues);
        }

        return "";
    }

    public getJobResultsUrl(jobRequest: TaskAgentJobRequest): string {
        const routeValues: { [key: string]: string } = {};

        if (jobRequest.planType.toLowerCase() === PlanTypes.Build) {
            // If we know this is a build, add the job ID to the query parameters and route to the build results page
            routeValues[QueryParams.Project] = jobRequest.scopeId;
            routeValues[QueryParams.BuildId] = jobRequest.owner.id.toString();
            routeValues[QueryParams.JobId] = jobRequest.jobId.toString();

            return routeUrl(this.pageContext, Routes.BuildResultRouteId, routeValues);
        }
        else if (jobRequest.owner && jobRequest.owner._links && jobRequest.owner._links.web) {
            // Otherwise, just try to go to the link on the object (if we have one)
            return jobRequest.owner._links.web.href;
        }

        return "";
    }

    public getQueueChecksUrl(agentQueue: TaskAgentQueue): string {
        const routeValues: { [key: string]: string } = this._getDefaultRouteValues();
        routeValues[QueryParams.QueueId] = agentQueue.id.toString();
        routeValues[QueryParams.TabId] = PoolHubViewKeys.Checks;

        return routeUrl(this.pageContext, Routes.ProjectSettingsRouteId, routeValues);
    }

    public getServiceConnectionUrl(elasticPool: ElasticPool): string {
        const routeValues: { [key: string]: string } = {};
        routeValues[QueryParams.Project] = elasticPool.serviceEndpointScope;
        routeValues[QueryParams.AdminPivot] = RouteValues.ServiceConnections;
        routeValues[QueryParams.ResourceId] = elasticPool.serviceEndpointId;

        return routeUrl(this.pageContext, Routes.ProjectSettingsRouteId, routeValues);
    }

    private _getDefaultRouteValues(): { [key: string]: string } {
        const routeValues: { [key: string]: string } = {};
        const projectDataService = this.pageContext.getService<IProjectDataService>("IProjectDataService");
        const projectData = projectDataService.getProjectData();

        if (projectData) {
            routeValues[QueryParams.Project] = projectData.name;
            routeValues[QueryParams.AdminPivot] = RouteValues.ProjectPools;
        } else {
            routeValues[QueryParams.AdminPivot] = RouteValues.AccountPools;
        }

        return routeValues;
    }
}

Services.add("IPoolAdminLinkingService", { serviceFactory: PoolAdminLinkingService });
